import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PromotionalPopup from "@/components/PromotionalPopup";
import CookieConsent from "@/components/CookieConsent";
import { SUPPORT_EMAIL, BRAND_NAME } from "@/config/appConfig";
import { 
  CheckCircle, 
  Zap, 
  Shield, 
  BarChart3, 
  Globe, 
  Users,
  ArrowRight,
  Check,
  Menu,
  X,
  LayoutDashboard,
  LogOut,
  Sparkles,
  MessageCircle,
  Send,
  Bot,
  User,
  Gift
} from "lucide-react";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));

// Helper function to apply brand colors as CSS variables
const applyBrandColors = (primaryColor, secondaryColor, accentColor) => {
  if (primaryColor) {
    document.documentElement.style.setProperty('--brand-primary', primaryColor);
  }
  if (secondaryColor) {
    document.documentElement.style.setProperty('--brand-secondary', secondaryColor);
  }
  if (accentColor) {
    document.documentElement.style.setProperty('--brand-accent', accentColor);
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // Default to monthly
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [enterpriseForm, setEnterpriseForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Brand settings state
  const [brandSettings, setBrandSettings] = useState({
    company_name: "SeatPilot",
    logo_url: null,
    logo_dark_url: null,
    primary_color: "#7c3aed",
    social_links: {},
    contact_email: "support@seatpilot.co.uk"
  });
  const [legalPages, setLegalPages] = useState([]);
  const [paygFees, setPaygFees] = useState({ setup_fee: 100, plan_switch_fee: 50 });
  const [dynamicPlans, setDynamicPlans] = useState(null);
  const [referralRewards, setReferralRewards] = useState({ referrer: 50, referred: 50 });

  // Fetch brand settings and PAYG fees on mount
  useEffect(() => {
    fetchBrandSettings();
    fetchPaygFees();
    fetchDynamicPlans();
    fetchReferralRewards();
  }, []);

  const fetchDynamicPlans = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscriptions/plans/public`);
      if (res.ok) {
        const data = await res.json();
        if (data.monthly && data.yearly) {
          setDynamicPlans(data);
        }
      }
    } catch (error) {
      console.error("Failed to fetch dynamic plans:", error);
    }
  };

  const fetchPaygFees = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform/payg-fees`);
      if (res.ok) {
        const data = await res.json();
        setPaygFees(data);
      }
    } catch (error) {
      console.error("Failed to fetch PAYG fees:", error);
    }
  };

  const fetchReferralRewards = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/referrals/info`);
      if (res.ok) {
        const data = await res.json();
        setReferralRewards({
          referrer: data.referrer_reward || 50,
          referred: data.referred_reward || 50
        });
      }
    } catch {}
  };

  const fetchBrandSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform/settings/public`);
      if (res.ok) {
        const data = await res.json();
        if (data.brand) {
          setBrandSettings(prev => ({ 
            ...prev, 
            ...data.brand,
            social_links: data.social || {}  // Map social to social_links
          }));
          // Apply brand colors as CSS variables
          applyBrandColors(
            data.brand.primary_color,
            data.brand.secondary_color,
            data.brand.accent_color
          );
        }
        if (data.legal_pages) {
          setLegalPages(data.legal_pages);
        }
      }
    } catch (error) {
      console.error("Failed to fetch brand settings:", error);
    }
  };

  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact/enterprise`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(enterpriseForm)
      });
      if (response.ok) {
        toast.success("Inquiry submitted! We'll be in touch soon.");
        setEnterpriseForm({ company_name: "", contact_name: "", contact_email: "", contact_phone: "", message: "" });
      } else {
        toast.error("Failed to submit. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setChatLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMessage,
          session_id: chatSessionId
        })
      });

      const data = await response.json();
      setChatSessionId(data.session_id);
      setChatMessages(prev => [...prev, { role: "assistant", content: data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { 
        role: "assistant", 
        content: `Sorry, I'm having trouble connecting. Please try again later or contact ${SUPPORT_EMAIL}` 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const features = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Drag & Drop Builder",
      description: "Create complex seating layouts in minutes with our intuitive visual editor."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Seat Holds",
      description: "Real-time seat reservation with automatic expiry and conflict resolution."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Embeddable Widget",
      description: "Add our seating selection widget to your website with a single line of code."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Analytics Dashboard",
      description: "Track bookings, seat availability, and revenue in real-time."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Team Management",
      description: "Invite team members and manage permissions across your organization."
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: "API Integration",
      description: "Connect with your existing ticketing system via our RESTful API."
    }
  ];

  // Get plans based on billing period
  const getPlans = () => {
    // Use dynamic plans from backend if available
    if (dynamicPlans) {
      const plansList = billingPeriod === "monthly" ? dynamicPlans.monthly : dynamicPlans.yearly;
      if (plansList && plansList.length > 0) {
        return plansList.map(plan => ({
          ...plan,
          cta: plan.isEnterprise ? "Contact Us" : (plan.popular ? "Subscribe Now" : (plan.id === "free_trial" ? "Start Free Trial" : "Get Started")),
          planId: plan.id,
          oneTimeFee: plan.id === "pay_as_you_go" && paygFees.setup_fee > 0 ? `£${paygFees.setup_fee} one-time setup fee` : null
        }));
      }
    }

    // Fallback to hardcoded plans
    const basePlans = [
      {
        name: "Free Trial",
        price: "£0",
        period: "for 1 month",
        description: "Perfect for testing",
        features: [
          "1 event",
          "1 layout",
          "Up to 500 seats",
          "Basic support"
        ],
        cta: "Start Free Trial",
        popular: false,
        planId: "free_trial"
      },
      {
        name: "Pay As You Go",
        price: "£0.50",
        period: "per seat",
        description: "For occasional events",
        features: [
          "Unlimited events",
          "Unlimited layouts",
          "Pay only for bookings",
          "No monthly fees",
          "Email support"
        ],
        cta: "Get Started",
        popular: false,
        planId: "pay_as_you_go",
        oneTimeFee: paygFees.setup_fee > 0 ? `£${paygFees.setup_fee} one-time setup fee` : null
      }
    ];

    // Add Monthly or Annual plan based on selection
    if (billingPeriod === "monthly") {
      basePlans.push({
        name: "Monthly",
        price: "£50",
        period: "per month",
        description: "For growing businesses",
        features: [
          "Unlimited events",
          "Unlimited layouts",
          "10,000 seats/month included",
          "£0.30/seat after limit",
          "Priority support"
        ],
        cta: "Subscribe Now",
        popular: true,
        planId: "monthly"
      });
    } else {
      basePlans.push({
        name: "Annual",
        price: "£480",
        originalPrice: "£600",
        period: "per year",
        description: "Save 20% with annual billing",
        features: [
          "Unlimited events",
          "Unlimited layouts",
          "10,000 seats/month included",
          "£0.30/seat after limit",
          "Priority support",
          "14-day money-back guarantee"
        ],
        cta: "Subscribe & Save",
        popular: true,
        savings: "Save £120",
        planId: "monthly_yearly"
      });
    }

    // Enterprise plan
    basePlans.push({
      name: "Enterprise",
      price: "Custom",
      period: "pricing",
      description: "For large-scale operations",
      features: [
        "Custom seat allowance",
        "Custom pricing per seat",
        "Dedicated support",
        "SLA guarantee",
        "White-label options",
        "Custom integrations"
      ],
      cta: "Contact Us",
      popular: false,
      isEnterprise: true,
      planId: "enterprise"
    });

    return basePlans;
  };

  const plans = getPlans();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">SP</span>
              </div>
              <span className="text-xl font-bold text-gray-900">SeatPilot</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Pricing</a>
              <a href="#referral" className="text-gray-600 hover:text-gray-900 text-sm font-medium flex items-center gap-1">
                <Gift className="w-4 h-4 text-violet-500" />
                Refer & Earn
              </a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Contact</a>
              
              {!loading && (
                isAuthenticated ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                        <span className="text-violet-600 font-medium text-sm">
                          {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
                        </span>
                      </div>
                      <span className="text-sm text-gray-700">{user?.name?.split(' ')[0] || 'User'}</span>
                    </div>
                    <Button size="sm" onClick={() => navigate('/dashboard')} className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Button>
                    <Button size="sm" variant="outline" onClick={logout} className="gap-2" title="Sign out">
                      <LogOut className="w-4 h-4" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={() => navigate('/login')}>Log In</Button>
                    <Button size="sm" onClick={() => navigate('/signup')} className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700">
                      Get Started
                    </Button>
                  </>
                )
              )}
            </div>

            {/* Mobile menu button */}
            <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4">
            <div className="flex flex-col gap-4">
              <a href="#features" className="text-gray-600 hover:text-gray-900">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <a href="#referral" className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
                <Gift className="w-4 h-4 text-violet-500" />
                Refer & Earn
              </a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900">Contact</a>
              {!loading && (
                isAuthenticated ? (
                  <>
                    <div className="flex items-center gap-2 py-2">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                        <span className="text-violet-600 font-medium text-sm">
                          {user?.name?.charAt(0) || user?.email?.charAt(0) || "?"}
                        </span>
                      </div>
                      <span className="text-gray-700">{user?.name || user?.email}</span>
                    </div>
                    <Button onClick={() => navigate('/dashboard')} className="bg-gradient-to-r from-violet-600 to-indigo-600 gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Go to Dashboard
                    </Button>
                    <Button variant="outline" onClick={logout} className="gap-2">
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => navigate('/login')}>Log In</Button>
                    <Button onClick={() => navigate('/signup')} className="bg-gradient-to-r from-violet-600 to-indigo-600">Get Started</Button>
                  </>
                )
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              Powered by TicketKart
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Seating Charts Made
              <span className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent"> Simple</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Create beautiful, interactive seating charts for your events. Integrate with your ticketing platform in minutes with our powerful API.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <Button 
                  size="lg" 
                  onClick={() => navigate('/dashboard')}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 gap-2"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Go to Dashboard
                </Button>
              ) : (
                <Button 
                  size="lg" 
                  onClick={() => navigate('/signup')}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 gap-2"
                >
                  Start Free Trial <ArrowRight className="w-4 h-4" />
                </Button>
              )}
              <Button size="lg" variant="outline" onClick={() => navigate('/demo')}>
                View Demo
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="text-sm text-gray-500 mt-4">No credit card required • 1 month free trial</p>
            )}
          </div>

          {/* Hero Image/Preview */}
          <div className="mt-16 relative">
            <div className="bg-gradient-to-b from-violet-50 to-white rounded-2xl p-4 sm:p-8 shadow-xl border border-gray-100">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-[300px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="flex justify-center gap-2 mb-4">
                      {[...Array(10)].map((_, i) => (
                        <div key={i} className={`w-8 h-8 rounded-lg ${i % 3 === 0 ? 'bg-violet-500' : i % 3 === 1 ? 'bg-indigo-400' : 'bg-gray-300'}`}></div>
                      ))}
                    </div>
                    <div className="flex justify-center gap-2 mb-4">
                      {[...Array(12)].map((_, i) => (
                        <div key={i} className={`w-8 h-8 rounded-lg ${i % 4 === 0 ? 'bg-violet-500' : 'bg-gray-300'}`}></div>
                      ))}
                    </div>
                    <div className="flex justify-center gap-2">
                      {[...Array(10)].map((_, i) => (
                        <div key={i} className={`w-8 h-8 rounded-lg ${i % 2 === 0 ? 'bg-indigo-400' : 'bg-gray-300'}`}></div>
                      ))}
                    </div>
                    <div className="mt-6 bg-gray-800 text-white px-8 py-2 rounded-lg inline-block text-sm">
                      STAGE
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A complete seating chart solution built for modern event ticketing platforms.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center text-violet-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-8">
              Choose the plan that fits your needs. Start free and scale as you grow.
            </p>
            
            {/* Monthly/Yearly Toggle */}
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === "monthly"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod("yearly")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === "yearly"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                Yearly
                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`p-6 relative ${plan.popular ? 'border-2 border-violet-500 shadow-lg' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                {plan.savings && (
                  <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                    {plan.savings}
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    {plan.originalPrice && (
                      <span className="text-gray-400 line-through text-lg mr-1">{plan.originalPrice}</span>
                    )}
                    <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    <span className="text-gray-500 text-sm">{plan.period}</span>
                  </div>
                  <p className="text-gray-500 text-sm mt-2">{plan.description}</p>
                  {plan.oneTimeFee && (
                    <p className="text-orange-600 text-xs font-medium mt-1 flex items-center gap-1">
                      <span className="bg-orange-100 px-2 py-0.5 rounded">{plan.oneTimeFee}</span>
                    </p>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${plan.popular ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => plan.isEnterprise ? document.getElementById('contact').scrollIntoView({ behavior: 'smooth' }) : navigate('/signup')}
                >
                  {plan.cta}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
      <section id="referral" className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-4 py-2 rounded-full text-violet-600 font-medium text-sm mb-4">
              <Gift className="w-4 h-4" />
              Refer & Earn Program
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Share SeatPilot, Earn Rewards
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Refer your friends and colleagues to SeatPilot. When they subscribe, you both get credit for White-Label subscription!
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-violet-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Share Your Link</h3>
              <p className="text-gray-600 text-sm">Sign up and get your unique referral link to share with friends</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-violet-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">They Subscribe</h3>
              <p className="text-gray-600 text-sm">When your friend signs up and subscribes to any paid plan</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-12 h-12 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-xl font-bold text-violet-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Both Get Rewarded</h3>
              <p className="text-gray-600 text-sm">You and your friend both receive credit for White-Label!</p>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg max-w-2xl mx-auto text-center">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-violet-600">£{referralRewards.referrer}</p>
                <p className="text-sm text-gray-500">You Get</p>
              </div>
              <div className="text-3xl text-gray-300">+</div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-600">£{referralRewards.referred}</p>
                <p className="text-sm text-gray-500">Friend Gets</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">Credit for <strong>White-Label</strong> subscription!</p>
            <Button 
              size="lg" 
              onClick={() => isAuthenticated ? navigate('/dashboard?tab=referrals') : navigate('/signup')} 
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-lg px-8"
            >
              Start Referring Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-xs text-gray-400 mt-4">
              Credit can only be used for White-Label subscription. Expires in 6 months if unused.
            </p>
          </div>
        </div>
      </section>

      {/* Enterprise Contact Section */}
      <section id="contact" className="py-20 bg-gray-50 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Enterprise Inquiries</h2>
            <p className="text-gray-600">
              Need a custom solution? Let&apos;s discuss your requirements.
            </p>
          </div>
          <Card className="p-8">
            <form onSubmit={handleEnterpriseSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                  <Input 
                    value={enterpriseForm.company_name}
                    onChange={(e) => setEnterpriseForm({...enterpriseForm, company_name: e.target.value})}
                    placeholder="Your company"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                  <Input 
                    value={enterpriseForm.contact_name}
                    onChange={(e) => setEnterpriseForm({...enterpriseForm, contact_name: e.target.value})}
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <Input 
                  type="email"
                  value={enterpriseForm.contact_email}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, contact_email: e.target.value})}
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone / Mobile Number *</label>
                <Input 
                  type="tel"
                  value={enterpriseForm.contact_phone}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, contact_phone: e.target.value})}
                  placeholder="+44 20 1234 5678"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tell us about your needs</label>
                <Textarea 
                  value={enterpriseForm.message}
                  onChange={(e) => setEnterpriseForm({...enterpriseForm, message: e.target.value})}
                  placeholder="Expected number of events, seats per event, integration requirements..."
                  rows={4}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Inquiry"}
              </Button>
            </form>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {brandSettings.logo_dark_url || brandSettings.logo_url ? (
                  <img 
                    src={brandSettings.logo_dark_url || brandSettings.logo_url} 
                    alt={brandSettings.company_name}
                    className="h-8 object-contain"
                  />
                ) : (
                  <>
                    <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-indigo-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">SP</span>
                    </div>
                    <span className="text-xl font-bold">{brandSettings.company_name || "SeatPilot"}</span>
                  </>
                )}
              </div>
              <p className="text-gray-400 text-sm">Seating charts made simple for modern event ticketing.</p>
              
              {/* Social Links */}
              {brandSettings.social_links && Object.keys(brandSettings.social_links).length > 0 && (
                <div className="flex gap-3 mt-4">
                  {brandSettings.social_links.twitter && (
                    <a href={brandSettings.social_links.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                  )}
                  {brandSettings.social_links.facebook && (
                    <a href={brandSettings.social_links.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </a>
                  )}
                  {brandSettings.social_links.linkedin && (
                    <a href={brandSettings.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </a>
                  )}
                  {brandSettings.social_links.instagram && (
                    <a href={brandSettings.social_links.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                    </a>
                  )}
                </div>
              )}
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><Link to="/developer-docs" className="hover:text-white">Developer Docs</Link></li>
                <li><a href="/api/docs" className="hover:text-white">API Reference</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-white">Cookie Policy</Link></li>
                {legalPages.filter(p => p.is_published && !['privacy', 'terms', 'cookies'].includes(p.slug)).map(page => (
                  <li key={page.slug}><Link to={`/${page.slug}`} className="hover:text-white">{page.title}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="#contact" className="hover:text-white">Contact Us</a></li>
                <li>{brandSettings.contact_email || SUPPORT_EMAIL}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} {brandSettings.company_name || BRAND_NAME}. Powered by TicketKart. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* AI Chat Widget */}
      <div className="fixed bottom-20 right-6 z-50">
        {/* Chat Button */}
        {!showChat && (
          <button
            onClick={() => setShowChat(true)}
            className="w-14 h-14 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
          >
            <MessageCircle className="w-6 h-6 text-white" />
          </button>
        )}

        {/* Chat Window */}
        {showChat && (
          <Card className="w-80 sm:w-96 h-[500px] flex flex-col shadow-2xl border-0">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white p-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-semibold">SeatPilot Assistant</h4>
                  <p className="text-xs text-violet-200">Ask me anything!</p>
                </div>
              </div>
              <button 
                onClick={() => setShowChat(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {chatMessages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 text-violet-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    Hi! I&apos;m here to help you learn about SeatPilot. Ask me about features, pricing, or how to get started!
                  </p>
                </div>
              )}
              {chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-violet-600" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'user' 
                      ? 'bg-violet-600 text-white' 
                      : 'bg-white border border-gray-200 text-gray-700'
                  }`}>
                    {msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                  )}
                </div>
              ))}
              {chatLoading && (
                <div className="flex gap-2">
                  <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-violet-600" />
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <form onSubmit={handleSendChatMessage} className="p-4 border-t bg-white rounded-b-lg">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder="Type your message..."
                  disabled={chatLoading}
                  className="flex-1"
                />
                <Button 
                  type="submit" 
                  size="icon"
                  disabled={chatLoading || !chatInput.trim()}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>

      {/* Cookie Consent Banner - Must accept before using site */}
      <CookieConsent />

      {/* Promotional Popup */}
      <PromotionalPopup page="home" isLoggedIn={isAuthenticated} />
    </div>
  );
}
